/**
 * Labstep
 *
 * @module containers/Entity/Read/Count
 * @desc Container for reading entities count
 */

import type { APICallOptions } from 'labstep-web/models';
import { readEntitiesCount } from 'labstep-web/state/actions/entity';
import { readNotificationsCount } from 'labstep-web/state/actions/notification';
import {
  selectCursorTotal,
  selectReadCursorStatus,
} from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  IEntityReadCountContainerChildrenProps,
  IEntityReadCountContainerProps,
} from './types';

/**
 * Read Count Container connecting a component with state
 * Returns orginal component with
 * read function, status of action, and total count
 */
export const EntityReadCountContainer: React.FC<
  IEntityReadCountContainerProps
> = ({ children, entityName, params }) => {
  const dispatch = useDispatch();

  const countParams = { ...params, get_count: 1 };

  // Use selectors to get state
  const status = useSelector((state) =>
    selectReadCursorStatus(state, entityName, countParams),
  );
  const total = useSelector((state) =>
    selectCursorTotal(state, entityName, countParams),
  );

  /**
   * Read entities count - memoized to prevent unnecessary re-renders
   * @param  {object} options - Additional options (onSucces, onFail e.t.c)
   */
  const read = useCallback(
    (options?: APICallOptions) => {
      if (entityName === 'notification') {
        dispatch(readNotificationsCount());
      } else {
        dispatch(readEntitiesCount(entityName, params, options));
      }
    },
    [dispatch, entityName, params],
  );

  return children({
    status,
    total,
    read,
  });
};
